import React, { useState, useEffect } from 'react'
import { Button, Spin, Modal, Card, List, Typography, Tag, Avatar, Space } from 'antd'
import { UserOutlined, LogoutOutlined, ClockCircleOutlined, EyeOutlined } from '@ant-design/icons'
import { useSelector } from 'react-redux'
import { useDispatch } from 'react-redux'

import { authslice } from '../store/auth/authSlice'
import Layout from '../components/layout/Layout'
import axiosInstance from '../utils/axiosInstance'

const { Title, Text, Paragraph } = Typography

function Home() {
  const { logout } = authslice.actions
  const dispatch = useDispatch()

  const [loading, setLoading] = useState(false)
  const [connectedUsers, setConnectedUsers] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [hoveredMemberId, setHoveredMemberId] = useState(null) // State to handle hover logic inline

  const userType = useSelector((state) => state.auth.user.type)

  const getOnlinePeoples = async () => {
    try {
      setLoading(true)
      const response = await axiosInstance.get(`/api/v1/logs/currentOnlinePeoples`)
      setConnectedUsers(response.data?.users || [])
      setLoading(false)
    } catch (e) {
      console.log(e)
      setLoading(false)
    }
  }

  useEffect(() => {
    getOnlinePeoples()
  }, [])

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      dispatch(logout())
    }
  }

  return (
    <Layout style={{ padding: '20px' }}>
      <Space direction="vertical" size="middle" style={{ width: '100%', maxWidth: 600 }}>
        <Card>
          <Title level={3} style={{ marginBottom: 4, fontWeight: 'bold', color: '#2d2d2d' }}>
            Active Members{' '}
            <Text type="secondary" style={{ fontSize: 16, marginLeft: 8 }}>
              {connectedUsers.length > 0 && connectedUsers.length + ' active now'}
            </Text>
          </Title>
        </Card>
        <Card>
          {loading && (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Spin tip="Loading members..." size="large" />
            </div>
          )}

          {!loading && connectedUsers.length === 0 && (
            <div style={{ textAlign: 'center', padding: 30, color: '#8c8c8c' }}>
              <UserOutlined style={{ fontSize: 48, marginBottom: 10, display: 'block' }} />
              <p>There are no users online at the moment.</p>
            </div>
          )}

          {!loading && (
            <>
              <Space direction="vertical" style={{ width: '100%' }}>
                {connectedUsers.map((el) => (
                  <List.Item key={el.userId}>
                    <a
                      href={`/member/${el.userId}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        textDecoration: 'none',
                        color: 'inherit',
                        transition: 'transform 0.2s ease-in-out',
                      }}
                      onMouseEnter={() => setHoveredMemberId(el.userId)}
                      onMouseLeave={() => setHoveredMemberId(null)}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                        <Avatar style={{ backgroundColor: '#1890ff', color: '#fff', marginRight: 12 }} icon={<UserOutlined />} />

                        <div>
                          <div style={{ fontWeight: 600, fontSize: '15px' }}>
                            {el.firstName} {el.lastName}
                          </div>
                          <Text type="secondary" style={{ fontSize: 12, marginTop: 4 }}>
                            Joined: <span style={{ opacity: 0.8 }}>{el.joinedAt?.name || 'Unknown'}</span>
                          </Text>
                        </div>

                        <Tag color="green" style={{ marginLeft: 'auto', fontSize: 12, padding: '0 6px', height: '20px' }}>
                          <span style={{ fontSize: 8, fontWeight: 'bold' }}>.</span> ONLINE
                        </Tag>
                      </div>
                    </a>
                  </List.Item>
                ))}
              </Space>

              {/* Show Modal Link */}
              {connectedUsers.length > 0 && (
                <Button type="text" htmlType="button" style={{ textAlign: 'left', width: '100%', marginTop: 8, color: '#666' }} onClick={() => setIsModalOpen(true)}>
                  View detailed directory{' '}
                  <span style={{ marginLeft: 8 }}>
                    <EyeOutlined />
                  </span>
                </Button>
              )}
            </>
          )}
        </Card>

        {/* Action Area (Only for Admins/Coaches) */}
        {(userType === 'super-admin' || userType == 'regular-admin' || userType == 'coach') && (
          <div style={{ marginTop: 20, textAlign: 'center' }}>
            <Button type="text" danger icon={<LogoutOutlined />} onClick={handleLogout} style={{ background: '#fff', borderRadius: '8px' }}>
              Logout
            </Button>
          </div>
        )}
      </Space>

      <Modal
        title={
          <>
            <Title level={4} style={{ marginBottom: 0 }}>
              Online Members Directory
            </Title>
          </>
        }
        open={isModalOpen}
        footer={null}
        onCancel={() => setIsModalOpen(false)}
        bodyStyle={{ padding: 20 }}
        className="modal-overlay"
      >
        <p style={{ fontSize: '14px', color: '#555', marginBottom: 16, fontStyle: 'italic' }}>Click on a member's name to open their profile.</p>

        {loading ? (
          <div style={{ textAlign: 'center' }}>
            <Spin />
          </div>
        ) : connectedUsers.length === 0 ? (
          <div>No users found online.</div>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }}>
            {connectedUsers.map((el) => (
              <List.Item key={el.userId}>
                <a
                  href={`/member/${el.userId}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    textDecoration: 'none',
                    color: '#1677ff',
                    transition: 'transform 0.2s ease-in-out',
                  }}
                  onMouseEnter={() => setHoveredMemberId(el.userId)}
                  onMouseLeave={() => setHoveredMemberId(null)}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar style={{ backgroundColor: '#1890ff', color: '#fff' }} icon={<UserOutlined />} />

                    <div>
                      <div style={{ fontWeight: 600, fontSize: '15px' }}>
                        {el.firstName} {el.lastName}
                      </div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Member at {el.joinedAt?.name || ''}
                      </Text>
                    </div>

                    <Tag color="green" style={{ marginLeft: 'auto', fontSize: 10, padding: '0 6px' }}>
                      ONLINE
                    </Tag>
                  </div>
                </a>
              </List.Item>
            ))}
          </Space>
        )}
      </Modal>
    </Layout>
  )
}

export default Home
